// Exclusion-RA draft previews (slice 1). Mirrors server/src/routes/raDrafts.ts.

import { apiFetch } from '@/lib/projectsApi'

export type ChangeType = 'unchanged' | 'added' | 'replaced' | 'removed_by_explicit_evidence' | 'conflicting' | 'unknown'
export type ReviewStatus = 'pending' | 'accepted' | 'rejected' | 'edited'
export type GateRouting = 'exclusion_candidate' | 'target' | 'further_review' | 'out_of_scope' | 'unreviewed' | 'blocked'
export type EvidenceScope = 'vessel' | 'context'
export type ValueState = 'confirmed' | 'provisional' | 'unconfirmed' | 'missing'
export type OverallState = 'confirmed' | 'provisional' | 'unresolved'
export type DraftStatus = 'dry_run' | 'preparing' | 'prepared' | 'in_review' | 'approved' | 'finalized' | 'discarded' | 'failed'

export interface DraftGenerationEvent {
  at?: string
  action?: string
  by?: string | null
  error?: string
  note?: string | null
  field?: string
  cost_usd?: number
  estimated_cost_usd?: number
  max_cost_usd?: number
  dropped_fields?: string[]
  [key: string]: unknown
}

export interface DraftReviewEvent {
  id: string
  review_status: ReviewStatus
  reviewed_evidence_ids: string[]
  review_reason: string | null
  review_note: string | null
  previous_status: ReviewStatus | null
  reviewer: string | null
  created_at: string
}

export interface ClassScopeResult {
  status: 'applicable' | 'ip_interface' | 'not_applicable' | 'unresolved' | 'no_list'
  profile_code: string
  class_society: string
  stored_function: string | null
  function_under_profile: string | null
  match_action: string
  reason: string
  warnings: string[]
}

export interface DraftCandidate {
  id: string
  run_id: string
  system_name: string
  maker: string | null
  model: string | null
  ship_function: string | null
  cbs: string | null
  cbs_source: string | null
  ip: string | null
  ip_basis: string | null
  target: string | null
  status: string
  promoted_project_system_id: string | null
  active_drafts: number
  gate: { ok: boolean; routing: GateRouting; reasons: string[]; warnings: string[]; class_scope: ClassScopeResult | null }
}

export interface DraftProfileOption { profile_code: string; class_society: string; position: number; display_name: string }

export interface DraftCandidatesResponse {
  profile: { profile_code: string; class_society: string; function_list_size: number } | null
  profiles: DraftProfileOption[]
  summary: Partial<Record<GateRouting, number>>
  rows: DraftCandidate[]
}

export interface DraftEvidence {
  evidence_id: string
  kind: string
  scope: EvidenceScope
  source_table: string | null
  source_id: string | null
  page: number | null
  title: string
  excerpt: string | null
  payload: Record<string, unknown>
}

export interface Citation {
  evidence_id: string
  quote: string | null
  page: number | null
  verified: boolean
  scope: EvidenceScope | null
  page_mismatch?: boolean
}

export interface DraftClaim {
  id: string
  ordering: number
  field: string
  baseline_value: unknown
  proposed_value: unknown
  change_type: ChangeType
  citations: Citation[]
  evidence_ids: string[]
  reasoning: string | null
  confidence: number
  requires_review: boolean
  validation_notes: string[]
  review_status: ReviewStatus
  reviewed_value: unknown
  reviewed_evidence_ids: string[]
  review_reason: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  review_note: string | null
  review_history?: DraftReviewEvent[]
}

export interface GradeStep {
  value: string | number | null
  rule: string
  inputs: Record<string, unknown>
  state: ValueState
  baseline: string | number | null
  disagrees_with_baseline: boolean
  reasons: string[]
}

export interface IacsComputed {
  family: 'iacs_base'
  version: string
  overall_state: OverallState
  category: GradeStep; cx: GradeStep; cy: GradeStep; as: GradeStep; h: GradeStep
  likelihood: GradeStep; impact: GradeStep; risk_level: GradeStep; treatment: GradeStep
  acceptance: {
    essential_all_met: boolean | null
    additional_all_met: boolean | null
    per_criterion: Array<{ field: string; status: string | null; state: ValueState; derived_from_connectivity?: boolean }>
    result: 'exclusion_supported' | 'not_eligible' | 'unresolved' | 'provisional'
    provisional_result: 'exclusion_supported' | 'not_eligible' | 'unresolved'
    reasons: string[]
  }
  unresolved: string[]
}

export interface DnvComputed {
  family: 'dnv'
  version: string
  overall_state: OverallState
  essential: Array<{ field: string; status: string | null; state: ValueState; derived_from_connectivity?: boolean }>
  additional: Array<{ field: string; status: string | null; state: ValueState; derived_from_connectivity?: boolean }>
  result: 'negligible_risk' | 'target' | 'unresolved' | 'provisional'
  provisional_result: 'negligible_risk' | 'target' | 'unresolved'
  reasons: string[]
  unresolved: string[]
}

export type Computed = IacsComputed | DnvComputed

export interface DraftPayload {
  draft: {
    id: string | null
    project_id: string
    compliance_profile_code: string
    class_society: string
    screening_system_id: string | null
    project_system_id: string | null
    candidate_name: string
    candidate_maker: string | null
    candidate_model: string | null
    candidate_function: string | null
    status: DraftStatus
    prompt_version: string
    model: string
    prompt_tokens: number
    completion_tokens: number
    cost_usd: number
    max_cost_usd?: number | null
    estimated_cost_usd?: number | null
    created_at?: string
    updated_at?: string
    failed_reason?: string | null
    generation_history?: DraftGenerationEvent[]
  }
  gate: { ok: boolean; routing: GateRouting; reasons: string[]; warnings: string[]; class_scope: ClassScopeResult | null; facts: Record<string, unknown> }
  baseline: {
    assessment_id: string
    system_name: string
    supplier: string | null
    source_hull_number: string | null
    class_society: string | null
    compliance_profile_code: string | null
  } | null
  baseline_match: { chosen: { tier: string; score: number; reasoning: string } | null; candidates: Array<{ assessment_id: string; system_name: string; supplier: string | null; tier: string; score: number; reasoning: string; source_hull_number: string | null }> } | null
  evidence: DraftEvidence[]
  evidence_notes: string[]
  claims: DraftClaim[]
  computed: Computed | null
  dropped_fields?: string[]
}

export interface DraftListItem {
  id: string
  compliance_profile_code: string
  class_society: string
  screening_system_id: string | null
  candidate_name: string
  candidate_maker: string | null
  status: DraftStatus
  overall_state: OverallState | null
  prompt_version: string
  model: string
  cost_usd: number
  max_cost_usd: number | null
  estimated_cost_usd: number | null
  created_at: string
  updated_at: string
  claim_count: number
  pending_count: number
  review_required_pending: number
}

export interface ReviewBody {
  review_status: ReviewStatus
  reviewed_value?: unknown
  reviewed_evidence_ids?: string[]
  review_reason?: string | null
  note?: string | null
}

export const raDraftsApi = {
  candidates: (projectId: string, profileCode?: string | null) =>
    apiFetch<DraftCandidatesResponse>('GET', `/api/projects/${projectId}/ra-drafts/candidates${profileCode ? `?profile_code=${encodeURIComponent(profileCode)}` : ''}`),
  list: (projectId: string) => apiFetch<DraftListItem[]>('GET', `/api/projects/${projectId}/ra-drafts`),
  prepare: (projectId: string, body: { screening_system_id: string; profile_code?: string | null; confirmed: true; max_cost_usd: number; dry_run?: boolean; supersede?: boolean }) =>
    apiFetch<DraftPayload>('POST', `/api/projects/${projectId}/ra-drafts/prepare`, body),
  get: (draftId: string) => apiFetch<DraftPayload>('GET', `/api/ra-drafts/${draftId}`),
  review: (draftId: string, claimId: string, body: ReviewBody) =>
    apiFetch<DraftPayload>('PATCH', `/api/ra-drafts/${draftId}/claims/${claimId}`, body),
  discard: (draftId: string) => apiFetch<{ ok: true }>('POST', `/api/ra-drafts/${draftId}/discard`),
}
